/**
 * Volume profile (volume-by-price) — pure, ATR-normalized, as-of safe.
 *
 * Bins transacted volume by PRICE over a trailing window, distributing each bar's volume
 * across the price range it spanned. Used to find the POC (point of control), HVNs
 * (high-volume nodes = price magnets / strong S/R) and LVNs (low-volume nodes = fast zones),
 * and a normalized volume_strength ∈ [0,1] at any price.
 *
 * Design guarantees (match pivots.ts / levels.ts):
 *  - Pure, no I/O, no hidden global state.
 *  - AS-OF SAFE: only the bars passed in are used. A backtester querying the profile as-of
 *    bar t passes bars[0..t]; nothing downstream of t leaks in. The `lookbackBars` window is
 *    applied to the TAIL (most recent bars) so intraday profiles reflect CURRENT participants,
 *    not a multi-day pile.
 *  - VOLATILITY-NORMALIZED: bin width is ATR-relative (or % fallback), never fixed dollars.
 *  - SPIKE-ROBUST: each bar's volume is winsorized (capped at a percentile of the window's bar
 *    volumes) so one halt-driven spike on a low-float name can't single-handedly define the POC.
 */
import type { OHLCVBar } from '@/lib/signals/doubleBottomDetector';

export interface VolumeBin { price: number; volume: number; volume_strength: number; }
export interface VolumeProfile {
  bins: VolumeBin[];          // ascending by price
  poc: number;                // price of the max-volume bin (0 when empty)
  pocVolume: number;
  hvns: number[];             // high-volume node prices (peaks above the HVN threshold)
  lvns: number[];             // low-volume node prices (troughs below the LVN threshold)
  binWidth: number;
  maxVolume: number;
  lookbackUsed: number;       // # bars actually used (for transparency / as-of audit)
}

export interface VolumeProfileConfig {
  binAtrMult: number;         // bin width = binAtrMult × ATR
  fallbackPct: number;        // bin width = fallbackPct% of price when ATR unavailable
  lookbackBars: number;       // trailing window; 0 = use all passed bars (long window = daily only)
  hvnPercentile: number;      // a bin is "high volume" at/above this percentile of bin volumes
  lvnPercentile: number;      // "low volume" at/below this percentile
  spikeWinsorPct: number;     // cap each bar's volume at this percentile of the window's volumes
}

export const DEFAULT_VP_CONFIG: VolumeProfileConfig = {
  binAtrMult: 0.25,
  fallbackPct: 0.1,
  lookbackBars: 0,
  hvnPercentile: 70,
  lvnPercentile: 20,
  spikeWinsorPct: 95,
};

export function computeVolumeProfile(
  barsIn: OHLCVBar[],
  cfg: VolumeProfileConfig = DEFAULT_VP_CONFIG,
  atr?: number | null,
): VolumeProfile {
  const empty: VolumeProfile = { bins: [], poc: 0, pocVolume: 0, hvns: [], lvns: [], binWidth: 0, maxVolume: 0, lookbackUsed: 0 };
  // Trailing window (as-of: the last N completed bars).
  const bars = cfg.lookbackBars > 0 && barsIn.length > cfg.lookbackBars ? barsIn.slice(-cfg.lookbackBars) : barsIn;
  if (bars.length < 2) return empty;

  const lastClose = bars[bars.length - 1]!.close;
  const bw = (atr && atr > 0) ? cfg.binAtrMult * atr : (cfg.fallbackPct / 100) * lastClose;
  if (!(bw > 0)) return empty;

  // Spike winsorization — cap each bar's volume at the spikeWinsorPct percentile of bar volumes.
  const vols = bars.map(b => b.volume ?? 0).filter(v => v > 0).sort((a, b) => a - b);
  const cap = vols.length ? percentile(vols, cfg.spikeWinsorPct) : Infinity;

  let minLow = Infinity, maxHigh = -Infinity;
  for (const b of bars) { if (b.low < minLow) minLow = b.low; if (b.high > maxHigh) maxHigh = b.high; }
  if (!(minLow > 0) || maxHigh <= minLow) return empty;

  const origin = Math.floor(minLow / bw) * bw;
  const nBins = Math.max(1, Math.ceil((maxHigh - origin) / bw) + 1);
  if (nBins > 5000) return empty;   // sanity guard
  const vol = new Array<number>(nBins).fill(0);

  for (const b of bars) {
    const v = Math.min(b.volume ?? 0, cap);
    if (!(v > 0)) continue;
    const L = b.low, H = b.high;
    if (H <= L) {                                  // flat bar — all volume to one bin
      vol[clampIdx(Math.floor((L - origin) / bw), nBins)]! += v;
      continue;
    }
    const span = H - L;
    const lo = clampIdx(Math.floor((L - origin) / bw), nBins);
    const hi = clampIdx(Math.floor((H - origin) / bw), nBins);
    for (let bi = lo; bi <= hi; bi++) {
      const binLow = origin + bi * bw, binHigh = binLow + bw;
      const overlap = Math.min(H, binHigh) - Math.max(L, binLow);
      if (overlap > 0) vol[bi]! += v * (overlap / span);
    }
  }

  let maxVolume = 0, pocIdx = 0;
  for (let i = 0; i < nBins; i++) if (vol[i]! > maxVolume) { maxVolume = vol[i]!; pocIdx = i; }
  if (!(maxVolume > 0)) return empty;

  const bins: VolumeBin[] = vol.map((v, i) => ({
    price: origin + i * bw + bw / 2,
    volume: v,
    volume_strength: v / maxVolume,
  }));

  // HVN / LVN nodes from the percentile of NON-ZERO bin volumes. A FLAT profile (no bin
  // stands meaningfully above the rest) yields NO nodes — otherwise every price would look
  // like an HVN. "Has spread" = the POC is at least 1.5× the median bin volume.
  const nz = bins.map(b => b.volume).filter(v => v > 0).sort((a, b) => a - b);
  const medBin = percentile(nz, 50);
  const hasSpread = nz.length > 2 && maxVolume > 1.5 * medBin;
  const hvns = hasSpread ? nodePeaks(bins, percentile(nz, cfg.hvnPercentile), 'high') : [];
  const lvns = hasSpread ? nodePeaks(bins.filter(b => b.volume > 0), percentile(nz, cfg.lvnPercentile), 'low') : [];

  return {
    bins,
    poc: bins[pocIdx]!.price,
    pocVolume: maxVolume,
    hvns,
    lvns,
    binWidth: bw,
    maxVolume,
    lookbackUsed: bars.length,
  };
}

/** volume_strength ∈ [0,1] at a given price (0 if outside the profile). */
export function volumeStrengthAt(profile: VolumeProfile, price: number): number {
  if (!profile.bins.length || !(profile.binWidth > 0)) return 0;
  const first = profile.bins[0]!.price - profile.binWidth / 2;
  const idx = Math.floor((price - first) / profile.binWidth);
  return idx >= 0 && idx < profile.bins.length ? profile.bins[idx]!.volume_strength : 0;
}

/** Nearest HVN price to `price` within `tol`, else null. */
export function nearestHvn(profile: VolumeProfile, price: number, tol: number): number | null {
  let best: number | null = null, bestD = Infinity;
  for (const h of profile.hvns) { const d = Math.abs(h - price); if (d <= tol && d < bestD) { bestD = d; best = h; } }
  return best;
}

// ── helpers ───────────────────────────────────────────────────────────────────
function percentile(sortedAsc: number[], p: number): number {
  if (!sortedAsc.length) return Infinity;
  const idx = Math.min(sortedAsc.length - 1, Math.max(0, Math.round((p / 100) * (sortedAsc.length - 1))));
  return sortedAsc[idx]!;
}
function clampIdx(i: number, n: number): number { return i < 0 ? 0 : i >= n ? n - 1 : i; }

/** Collapse consecutive bins past the threshold into nodes; return each run's peak/trough price. */
function nodePeaks(bins: VolumeBin[], thresh: number, dir: 'high' | 'low'): number[] {
  const out: number[] = [];
  let run: VolumeBin[] = [];
  const flush = () => {
    if (!run.length) return;
    const ext = dir === 'high'
      ? run.reduce((m, b) => (b.volume > m.volume ? b : m))
      : run.reduce((m, b) => (b.volume < m.volume ? b : m));
    out.push(ext.price);
    run = [];
  };
  for (const b of bins) {
    const inNode = dir === 'high' ? b.volume >= thresh : b.volume <= thresh;
    if (inNode) run.push(b); else flush();
  }
  flush();
  return out;
}
